import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDownloadURL, ref } from 'firebase/storage';
import PublicTrainingScreenshotBoard from '../../../../components/notes/PublicTrainingScreenshotBoard';
import { MIDDLE_SCHOOL_3D_PRINTING_2026_ID } from '../../../../utils/publicTrainingUpload';
import { storage } from '../../../../utils/firebase';

const TINKERCAD_PPT_EMBED_URL =
  'https://1drv.ms/p/c/f326a53b9af299ff/IQSshLoPwx1dT4wHFLLv8C0JAaCWv_WOnvJRQt5wXe6Hwm0?wdAr=1.7770833333333333';
const TINKERCAD_PPT_OPEN_URL =
  'https://1drv.ms/p/c/f326a53b9af299ff/IQSshLoPwx1dT4wHFLLv8C0JAaCWv_WOnvJRQt5wXe6Hwm0';

/** Storage에 업로드된 설치 파일 경로 (초등 연수와 동일 파일을 쓰려면 경로를 맞추면 됩니다.) */
const INSTALL_FILE_STORAGE_PATH = 'notes_public_training/elementary_3d_printing_2026/sculptris.exe';
const INSTALL_FILE_LABEL = 'Sculptris 설치 파일 다운로드';

export const trainingMetadata2026MiddleSchool3D = {
  id: 'middle-school-3d-printing-2026',
  title: '2026 「(중등) 3D프린팅 활용 직무연수」',
  date: '2026-04-09',
  path: '/teacher-training/digital/MiddleSchool3DPrintingTraining2026',
};

const MiddleSchool3DPrintingTraining2026: React.FC = () => {
  const navigate = useNavigate();
  const [installFileLink, setInstallFileLink] = useState<string | null>(null);
  const [installFileError, setInstallFileError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const loadInstallFileLink = async () => {
      try {
        const url = await getDownloadURL(ref(storage, INSTALL_FILE_STORAGE_PATH));
        if (mounted) {
          setInstallFileLink(url);
          setInstallFileError(null);
        }
      } catch (error) {
        console.warn('설치 파일 링크 조회 실패:', error);
        if (mounted) {
          setInstallFileLink(null);
          setInstallFileError('설치 파일을 찾지 못했습니다. Storage 경로를 확인해 주세요.');
        }
      }
    };
    loadInstallFileLink();
    return () => {
      mounted = false;
    };
  }, []);

  const linkButton = (href: string, label: string, external = true) => (
    <a
      href={href}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      style={{
        display: 'inline-block',
        marginTop: '8px',
        padding: '10px 18px',
        backgroundColor: 'var(--Color-fill-brand-color)',
        color: 'var(--Color-text-inverse)',
        textDecoration: 'none',
        borderRadius: '8px',
        fontWeight: 600,
        fontSize: '14px',
        fontFamily: 'var(--font-family-primary)',
      }}
    >
      {label} →
    </a>
  );

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100%',
        boxSizing: 'border-box',
        padding: '24px',
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        fontFamily: 'var(--font-family-primary)',
        backgroundColor: 'var(--Color-background-main)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <button
          type="button"
          onClick={() => navigate('/teacher-training/training-pages')}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--Color-fill-normal)',
            color: 'var(--Color-text-inverse)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          ← 돌아가기
        </button>
        <h1
          style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            margin: 0,
            color: 'var(--Color-text-strong)',
            lineHeight: 1.4,
          }}
        >
          {trainingMetadata2026MiddleSchool3D.title}
        </h1>
      </div>

      <div
        style={{
          marginBottom: '24px',
          border: '1px solid var(--Color-line-normal)',
          padding: '16px',
          borderRadius: '12px',
          backgroundColor: 'var(--Color-background-normal)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        <h2 style={{ fontWeight: 700, marginBottom: '12px', fontSize: '1.125rem', color: 'var(--Color-text-strong)' }}>
          목차
        </h2>
        <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
          {[
            ['#연수-개요', '연수 개요'],
            ['#순서-1', '1. 3D프린팅 현황 및 사용 방법'],
            ['#순서-2', '2. Sculpt 모델링 체험'],
            ['#순서-3', '3. Tinkercad 계정 생성 및 모델링하기'],
            ['#스크린샷-공유', '스크린샷 공유'],
          ].map(([hash, label]) => (
            <li key={hash} style={{ marginBottom: '8px' }}>
              <a
                href={hash}
                style={{
                  textDecoration: 'none',
                  color: 'var(--Color-fill-brand-color)',
                  fontWeight: 500,
                  fontSize: '15px',
                }}
              >
                {label}
              </a>
            </li>
          ))}
        </ul>
      </div>

      <h2
        id="연수-개요"
        style={{ fontWeight: 700, marginBottom: '12px', fontSize: '1.35rem', color: 'var(--Color-text-strong)' }}
      >
        연수 개요
      </h2>
      <div
        style={{
          backgroundColor: 'var(--Color-background-normal)',
          border: '1px solid var(--Color-line-normal)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px',
        }}
      >
        <p style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
          일시: 2026. 5. 11. 17:00 ~ 20:00
        </p>
        <p style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
          장소: 서울융합과학원
        </p>
      </div>
      <p style={{ color: 'var(--Color-text-normal)', marginBottom: '16px', lineHeight: 1.7, fontSize: '15px' }}>
        연수 순서는 다음과 같습니다.
      </p>
      <ol
        style={{
          color: 'var(--Color-text-normal)',
          marginBottom: '28px',
          lineHeight: 1.8,
          fontSize: '15px',
          paddingLeft: '22px',
        }}
      >
        <li>3D프린팅 현황 및 사용 방법</li>
        <li>Sculpt 모델링 체험하기</li>
        <li>Tinkercad 계정 생성 및 모델링하기</li>
      </ol>

      <h2
        id="순서-1"
        style={{ fontWeight: 700, marginBottom: '12px', fontSize: '1.35rem', color: 'var(--Color-text-strong)' }}
      >
        1. 3D프린팅 현황 및 사용 방법
      </h2>
      <p style={{ color: 'var(--Color-text-normal)', marginBottom: '12px', lineHeight: 1.7, fontSize: '15px' }}>
        모델 공유·프린터 생태계를 알아볼 때 아래 사이트를 참고해 보세요.
      </p>
      <ul style={{ marginBottom: '24px', lineHeight: 1.8, color: 'var(--Color-text-normal)', fontSize: '15px' }}>
        <li>
          <strong>MakerWorld</strong> — 3D 모델 다운로드 및 커뮤니티{' '}
          <a href="https://makerworld.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--Color-fill-brand-color)' }}>
            makerworld.com
          </a>
        </li>
        <li>
          <strong>Bambu Lab (한국)</strong> — 프린터·소재·지원 정보{' '}
          <a
            href="https://kr.store.bambulab.com/?gad_source=1&gad_campaignid=21867577812&gbraid=0AAAAA-b105J9ySYUATuSic1yHRmTvkJzh&gclid=EAIaIQobChMIseTD2qLgkwMVrX8PAh07gRphEAAYASAAEgIoR_D_BwE"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--Color-fill-brand-color)' }}
          >
            kr.store.bambulab.com
          </a>
        </li>
        <li>
          <strong>Thingiverse</strong> — 3D 모델 검색 및 다운로드{' '}
          <a href="https://www.thingiverse.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--Color-fill-brand-color)' }}>
            thingiverse.com
          </a>
        </li>
        <li>
          <strong>Revo Foods</strong> — 브랜드·제품 소개 쇼핑몰 예시{' '}
          <a href="https://shop-revo-foods.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--Color-fill-brand-color)' }}>
            shop-revo-foods.com
          </a>
        </li>
        <li>
          <strong>adidas (한국)</strong> — 제품 상세 페이지 예시 (클라이마쿨 레이스드){' '}
          <a
            href="https://www.adidas.co.kr/%ED%81%B4%EB%9D%BC%EC%9D%B4%EB%A7%88%EC%BF%A8-%EB%A0%88%EC%9D%B4%EC%8A%A4%EB%93%9C/KJ8969.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--Color-fill-brand-color)' }}
          >
            adidas.co.kr (KJ8969)
          </a>
        </li>
      </ul>
      <div style={{ marginBottom: '32px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
        {linkButton('https://makerworld.com', 'MakerWorld 열기')}
        {linkButton(
          'https://kr.store.bambulab.com/?gad_source=1&gad_campaignid=21867577812&gbraid=0AAAAA-b105J9ySYUATuSic1yHRmTvkJzh&gclid=EAIaIQobChMIseTD2qLgkwMVrX8PAh07gRphEAAYASAAEgIoR_D_BwE',
          'Bambu Lab Korea 열기'
        )}
        {linkButton('https://www.thingiverse.com/', 'Thingiverse 열기')}
        {linkButton('https://shop-revo-foods.com/', 'Revo Foods Shop')}
        {linkButton(
          'https://www.adidas.co.kr/%ED%81%B4%EB%9D%BC%EC%9D%B4%EB%A7%88%EC%BF%A8-%EB%A0%88%EC%9D%B4%EC%8A%A4%EB%93%9C/KJ8969.html',
          'adidas 제품 페이지'
        )}
      </div>

      <h2
        id="순서-2"
        style={{ fontWeight: 700, marginBottom: '12px', fontSize: '1.35rem', color: 'var(--Color-text-strong)' }}
      >
        2. Sculpt 모델링 체험하기
      </h2>
      <p style={{ color: 'var(--Color-text-normal)', marginBottom: '12px', lineHeight: 1.7, fontSize: '15px' }}>
        모델링 방식 중 Sculpt 방식을 다루는 프로그램들의 대표적인 예시입니다. 연수에서는 Sculptris 프로그램을 사용하여 간단한 3D 모델링을 체험해보고자 합니다.
      </p>
      <ul style={{ marginBottom: '16px', lineHeight: 1.8, color: 'var(--Color-text-normal)', fontSize: '15px' }}>
        <li>
          <strong>Nomad Sculpt</strong> —{' '}
          <a href="https://nomadsculpt.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--Color-fill-brand-color)' }}>
            nomadsculpt.com
          </a>
        </li>
        <li>
          <strong>ZBrush</strong> —{' '}
          <a
            href="https://www.maxon.net/en/zbrush"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--Color-fill-brand-color)' }}
          >
            maxon.net (ZBrush)
          </a>
        </li>
        <li>
          <strong>Blender</strong> —{' '}
          <a href="https://www.blender.org/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--Color-fill-brand-color)' }}>
            blender.org
          </a>
        </li>
      </ul>
      <div style={{ marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {linkButton('https://nomadsculpt.com/', 'Nomad Sculpt')}
        {linkButton('https://www.maxon.net/en/zbrush', 'ZBrush')}
        {linkButton('https://www.blender.org/', 'Blender')}
      </div>

      <div
        style={{
          backgroundColor: 'var(--Color-background-normal)',
          border: '1px dashed var(--Color-line-normal)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '32px',
        }}
      >
        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: 'var(--Color-text-strong)' }}>설치 파일</h3>
        <p style={{ margin: 0, fontSize: '14px', color: 'var(--Color-text-normal)', lineHeight: 1.6 }}>아래 버튼을 눌러 다운받아주세요.</p>
        {installFileLink ? (
          <div style={{ marginTop: '12px' }}>{linkButton(installFileLink, INSTALL_FILE_LABEL, true)}</div>
        ) : (
          <>
            <p style={{ margin: '12px 0 0 0', fontSize: '14px', color: 'var(--Color-text-normal)' }}>
              현재 등록된 설치 파일 링크가 없습니다.
            </p>
            {installFileError && (
              <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#b71c1c' }}>{installFileError}</p>
            )}
          </>
        )}
      </div>

      <h2
        id="순서-3"
        style={{ fontWeight: 700, marginBottom: '12px', fontSize: '1.35rem', color: 'var(--Color-text-strong)' }}
      >
        3. Tinkercad 계정 생성 및 모델링하기
      </h2>

      <div
        style={{
          marginBottom: '16px',
          padding: '16px',
          borderRadius: '12px',
          backgroundColor: 'var(--Color-background-normal)',
          border: '1px solid var(--Color-line-normal)',
        }}
      >
        <h3 style={{ margin: '0 0 8px 0', fontSize: '17px', color: 'var(--Color-text-strong)' }}>
          진행 안내
        </h3>
        <p
          style={{
            margin: 0,
            whiteSpace: 'pre-wrap',
            color: 'var(--Color-text-normal)',
            lineHeight: 1.7,
            fontSize: '15px',
          }}
        >
          계정 생성부터 기본 도형 모델링까지 아래 자료 순서대로 따라오면 됩니다.
        </p>
        <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '14px' }}>
          {linkButton('https://www.tinkercad.com', 'Tinkercad 열기')}
          {linkButton(TINKERCAD_PPT_OPEN_URL, 'PPT 새 창에서 열기')}
        </div>
        <div
          style={{
            marginTop: '16px',
            width: '100%',
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid var(--Color-line-normal)',
            backgroundColor: 'var(--Color-background-main)',
          }}
        >
          <iframe
            src={TINKERCAD_PPT_EMBED_URL}
            title="Tinkercad 계정 생성 및 모델링 자료"
            style={{ width: '100%', height: '720px', border: 'none' }}
            allowFullScreen
          />
        </div>
      </div>

      <h2
        id="스크린샷-공유"
        style={{ fontWeight: 700, marginBottom: '12px', fontSize: '1.35rem', color: 'var(--Color-text-strong)' }}
      >
        스크린샷 공유
      </h2>
      <p style={{ color: 'var(--Color-text-normal)', marginBottom: '12px', lineHeight: 1.7, fontSize: '15px' }}>
        실습 화면을 캡처해 올리고, 링크를 복사해 동료와 공유해 보세요.
      </p>
      <PublicTrainingScreenshotBoard trainingId={MIDDLE_SCHOOL_3D_PRINTING_2026_ID} />

    </div>
  );
};

export default MiddleSchool3DPrintingTraining2026;
